import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, getToken, removeToken } from '../api';

const AppContext = createContext(null);
const CART_KEY = 'cart_items';

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CART_KEY)) || []; } catch { return []; }
  });
  const [minOrderQty, setMinOrderQty] = useState(2);
  const [webCatalogPriceOffset, setWebCatalogPriceOffset] = useState(280);
  const [isDark, setIsDark] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches);

  const getCatalogUnitPrice = useCallback((product) => {
    const base = Number(product?.price) || 0;
    const off = Number(webCatalogPriceOffset) || 0;
    return Math.max(0, base - off);
  }, [webCatalogPriceOffset]);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  const refreshAppSettings = useCallback(() => api.settings.get().then((s) => {
    if (s?.min_order_quantity) setMinOrderQty(s.min_order_quantity);
    if (s?.web_catalog_price_offset != null) setWebCatalogPriceOffset(s.web_catalog_price_offset);
    return s;
  }).catch(() => {}), []);

  useEffect(() => { refreshAppSettings(); }, [refreshAppSettings]);

  const loadUserProfile = useCallback(async () => {
    setProfileLoading(true);
    try {
      const token = getToken();
      if (!token) { setUser(null); return; }
      const data = await api.auth.me();
      setUser({
        id: data.id,
        email: data.email,
        name: data.name || '',
        phone: data.phone || '',
        isAdmin: !!data.is_admin || !!data.is_superadmin,
        isSuperAdmin: !!data.is_superadmin,
      });
    } catch { setUser(null); }
    finally { setProfileLoading(false); }
  }, []);

  useEffect(() => { loadUserProfile(); }, [loadUserProfile]);

  const clearUser = useCallback(() => {
    removeToken();
    setUser(null);
    setCart([]);
    localStorage.removeItem(CART_KEY);
  }, []);

  const addToCart = useCallback((product, quantity) => {
    const qty = quantity ?? minOrderQty;
    setCart(prev => {
      const existing = prev.find(i => i.product.id === product.id);
      if (existing) return prev.map(i => i.product.id === product.id ? { ...i, quantity: i.quantity + qty } : i);
      const hasDifferentBrand = prev.length > 0 && prev[0].product.brand !== product.brand;
      if (hasDifferentBrand) return prev;
      return [...prev, { product, quantity: qty }];
    });
  }, [minOrderQty]);

  const removeFromCart = useCallback((productId) => setCart(prev => prev.filter(i => i.product.id !== productId)), []);
  const updateCartQuantity = useCallback((productId, quantity) => {
    if (quantity < 1) { removeFromCart(productId); return; }
    setCart(prev => prev.map(i => i.product.id === productId ? { ...i, quantity } : i));
  }, [removeFromCart]);
  const clearCart = useCallback(() => setCart([]), []);

  const cartTotal = cart.reduce((sum, i) => sum + getCatalogUnitPrice(i.product) * i.quantity, 0);
  const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0);

  const toggleTheme = () => setIsDark(p => !p);

  return (
    <AppContext.Provider value={{
      user, profileLoading, loadUserProfile, clearUser,
      cart, cartTotal, cartCount, addToCart, removeFromCart, updateCartQuantity, clearCart, minOrderQty,
      webCatalogPriceOffset, getCatalogUnitPrice, refreshAppSettings,
      isDark, toggleTheme,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useAppContext = () => useContext(AppContext);
